import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { ButtonT1 } from "../../components/atoms/Button";
import { Card } from "../../components/atoms/card";
import { todayCalendarDate } from "../../helper/dateTime";
import { usePlaceTimezone } from "../../helper/usePlaceTimezone";
import { useSetupGuideAutoOpen } from "../../app/onboarding/hooks/useSetupGuideAutoOpen";
import { useSetupGuide } from "../../app/onboarding/SetupGuideContext";
import {
  useDismissOperationsWelcomeMutation,
  useGetOperationsOverviewQuery,
} from "../../store/apiSlice/operationsApiSlice";
import {
  useGetOnboardingQuery,
  useUpdateOnboardingPreferencesMutation,
} from "../../store/apiSlice/onboardingApiSlice";
import { DashboardLayoutModal } from "./DashboardLayoutModal";
import "./operations.css";
import { OperationsComingUpCalendar } from "./OperationsComingUpCalendar";
import {
  availableDashboardCards,
  KPI_CARD_KEYS,
  loadDashboardLayout,
  PANEL_CARD_KEYS,
  resolveDashboardLayout,
  saveDashboardLayout,
  SNAPSHOT_CARD_KEYS,
} from "./operationsLayout";
import { OperationsHeaderActions } from "./OperationsHeaderActions";
import { OperationsNotesDrawer } from "./OperationsNotesDrawer";
import { OperationsTodayStrip, readTodayScope, saveTodayScope } from "./OperationsTodayStrip";
import { FirstVisitHint } from "../../app/onboarding/hints/FirstVisitHint";

/** @param {Object} props Shared application button properties. @return {JSX.Element} */
function OperationsButton({ children, onClick, ...props }) {
  return <ButtonT1 {...props} title={children} onClickCallback={onClick} />;
}

/** @const {Array<Object>} Visible domains and canonical operational labels. */
const domains = [
  {
    key: "sales",
    label: "Wholesale sales orders",
    permission: "can_view_sales_orders",
    state: "Awaiting confirmation",
  },
  {
    key: "purchases",
    label: "Purchase orders",
    permission: "can_view_purchase_orders",
    state: "Awaiting confirmation",
  },
  {
    key: "receipts",
    label: "Purchase receipts",
    permission: "can_view_receipts",
    state: "Waiting for approval",
  },
  {
    key: "logistics",
    label: "Upcoming shipments",
    permission: "can_view_shipments",
    state: "Next 30 days and overdue",
  },
];

/**
 * Format stored dates using company time, keeping calendar dates unchanged.
 * @param {?string} value Stored calendar date or timestamp.
 * @param {string} timezone Company timezone.
 * @return {string}
 */
export const operationsDate = (value, timezone) => {
  if (!value) return "Not scheduled";
  const calendar = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(calendar ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return "Not scheduled";
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: calendar ? "UTC" : timezone,
  }).format(date);
};

/**
 * Format authorised monetary values without inventing a currency.
 * @param {?string} amount Stored amount.
 * @param {?string} currency ISO currency code.
 * @return {string}
 */
export const operationsAmount = (amount, currency) => {
  if (amount == null || !currency || !Number.isFinite(Number(amount))) return "";
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency }).format(Number(amount));
  } catch {
    return "";
  }
};

/**
 * @param {string} type Attention record type.
 * @return {string}
 */
function recordLabel(type) {
  return type === "wholesale_sales_order"
    ? "Wholesale sales order"
    : type === "purchase_order"
      ? "Purchase order"
      : "Purchase receipt";
}

/**
 * @param {number} days Oldest queue age in days.
 * @return {string} Subtle emphasis class aligned with existing alert tokens.
 */
function oldestWaitingStateClass(days) {
  if (days == null || days < 3) return "";
  if (days < 7) return "operations-summary-state--aging";
  return "operations-summary-state--stale";
}

/**
 * @param {number} value Target count.
 * @param {number|string} motionKey Re-run animation when overview refreshes.
 * @return {number} Value shown in the KPI (animated when motion is allowed).
 */
function useAnimatedCount(value, motionKey) {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(target);
  const displayRef = useRef(target);

  useEffect(() => {
    displayRef.current = display;
  }, [display]);

  useEffect(() => {
    const reduced =
      process.env.NODE_ENV === "test" ||
      (typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    if (reduced) {
      setDisplay(target);
      displayRef.current = target;
      return undefined;
    }
    const from = displayRef.current;
    if (from === target) return undefined;
    const start = performance.now();
    const duration = 420;
    let frame = 0;
    const tick = now => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - progress) ** 3;
      const next = Math.round(from + (target - from) * eased);
      setDisplay(next);
      displayRef.current = next;
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, motionKey]);

  return display;
}

/** @param {Object} props KPI count display. @return {JSX.Element} */
function AnimatedCount({ value, motionKey }) {
  const display = useAnimatedCount(value, motionKey);
  return <>{display}</>;
}

/**
 * @param {Object} props Empty panel copy and optional navigation.
 * @return {JSX.Element}
 */
function OperationsEmptyPanel({ title, description, actionLabel, actionTo }) {
  return (
    <div className="operations-empty-panel">
      <svg className="operations-empty-panel-icon" viewBox="0 0 48 48" aria-hidden="true">
        <rect
          x="8"
          y="10"
          width="32"
          height="28"
          rx="4"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
        <path d="M16 20h16M16 26h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <p className="operations-empty-panel-title">{title}</p>
      {description && <p className="operations-empty-panel-description">{description}</p>}
      {actionLabel && actionTo && (
        <Link className="operations-empty-panel-action" to={actionTo}>
          {actionLabel}
        </Link>
      )}
    </div>
  );
}

/** @param {Array<{label: string, count: number, status: string}>} rows Status rows. @return {JSX.Element} */
function StatusBarRows({ rows }) {
  const max = Math.max(...rows.map(row => row.count), 1);
  return (
    <dl className="operations-status-bars">
      {rows.map(row => (
        <div key={row.status}>
          <dt>
            <span className="operations-status-bars-label">{row.label}</span>
            <span className="operations-status-bars-track" aria-hidden="true">
              <span
                className="operations-status-bars-fill"
                style={{ width: `${Math.max(4, (row.count / max) * 100)}%` }}
              />
            </span>
          </dt>
          <dd className="operations-badge">{row.count}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * @param {Array<{id: number, name: string, value: string}>} facilities Stock rows.
 * @param {?string} currency ISO currency.
 * @return {JSX.Element}
 */
/**
 * @param {Array<{currency: string, amount: string}>} outstanding Currency amounts.
 * @return {string}
 */
function formatOutstandingAmounts(outstanding) {
  if (!outstanding?.length) return "";
  return outstanding
    .map(item => operationsAmount(item.amount, item.currency))
    .filter(Boolean)
    .join(" · ");
}

/**
 * @param {{ count: number, outstanding?: Array<{amount: string}> }} row Payment status row.
 * @return {number}
 */
function paymentRowBarMetric(row) {
  if (!row.outstanding?.length) return row.count;
  if (row.outstanding.length === 1) {
    const amount = Number(row.outstanding[0].amount);
    return Number.isFinite(amount) && amount > 0 ? amount : row.count;
  }
  return row.count;
}

/** @param {Array<Object>} rows Payment status snapshot rows. @return {JSX.Element} */
function PaymentBarRows({ rows }) {
  const metrics = rows.map(paymentRowBarMetric);
  const max = Math.max(...metrics, 1);
  return (
    <dl className="operations-status-bars operations-payment-bars">
      {rows.map(row => {
        const metric = paymentRowBarMetric(row);
        const amountLabel = formatOutstandingAmounts(row.outstanding);
        return (
          <div key={row.status}>
            <dt>
              <span className="operations-status-bars-label">{row.label}</span>
              <span className="operations-status-bars-track" aria-hidden="true">
                <span
                  className="operations-status-bars-fill operations-payment-bars-fill"
                  style={{ width: `${Math.max(4, (metric / max) * 100)}%` }}
                />
              </span>
            </dt>
            <dd>
              <span className="operations-badge">{row.count}</span>
              {amountLabel && <span className="operations-payment-outstanding">{amountLabel}</span>}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function StockBarRows({ facilities, currency }) {
  const values = facilities.map(f => Number(f.value) || 0);
  const max = Math.max(...values, 1);
  return (
    <dl className="operations-status-bars operations-stock-bars">
      {facilities.map(facility => {
        const amount = Number(facility.value) || 0;
        return (
          <div key={facility.id}>
            <dt>
              <span className="operations-status-bars-label">{facility.name}</span>
              <span className="operations-status-bars-track" aria-hidden="true">
                <span
                  className="operations-status-bars-fill operations-stock-bars-fill"
                  style={{ width: `${Math.max(4, (amount / max) * 100)}%` }}
                />
              </span>
            </dt>
            <dd>{operationsAmount(facility.value, currency) || "Unavailable"}</dd>
          </div>
        );
      })}
    </dl>
  );
}

/** @param {Object} props Domain key for the compact KPI icon. @return {JSX.Element} */
function DomainIcon({ name }) {
  return (
    <span className="operations-summary-icon" aria-hidden="true">
      {name === "sales" && (
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M4 2.5h6.2L13 5.3V13.5H4V2.5Z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
          <path
            d="M10.2 2.5V5.3H13"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
          <path
            d="M6 8h4.2M6 10.4h4.2"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
      )}
      {name === "purchases" && (
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M3.2 4.2h9.6l-.8 6.6H4L3.2 4.2Z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
          <path
            d="M5.4 4.2V3.4A2.6 2.6 0 0 1 8 0.8a2.6 2.6 0 0 1 2.6 2.6v.8"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
      )}
      {name === "receipts" && (
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.3" />
          <path
            d="M5.1 8.2 7.1 10.2 11 6.2"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {name === "logistics" && (
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1.6 4.2h8.2v6.4H1.6V4.2Z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
          <path
            d="M9.8 6.2h2.6l1.8 2.2v2.2h-4.4V6.2Z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
          <circle cx="4.4" cy="11.8" r="1.2" stroke="currentColor" strokeWidth="1.3" />
          <circle cx="11.6" cy="11.8" r="1.2" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      )}
    </span>
  );
}

/** @param {Object} props Loading placeholder properties. @return {JSX.Element} */
function OperationsSkeleton() {
  return (
    <div role="status" aria-label="Loading operations overview" className="operations-loading">
      <div className="operations-summary">
        {[1, 2, 3, 4].map(key => (
          <div key={key} className="operations-skeleton" />
        ))}
      </div>
      <div className="operations-panels">
        <div className="operations-skeleton operations-skeleton-panel" />
        <div className="operations-skeleton operations-skeleton-panel" />
      </div>
      <span className="operations-visually-hidden">Loading operations overview</span>
    </div>
  );
}

/** @param {Object} props Queue data and company timezone. @return {JSX.Element} */
function AttentionRow({ item, timezone }) {
  const label = recordLabel(item.type);
  return (
    <tr
      className={`operations-record${item.priority === "high" ? " operations-record-priority" : ""}`}
    >
      <th scope="row">
        <span className="operations-eyebrow operations-record-heading">{label}</span>
        <strong>{item.number}</strong>
      </th>
      <td>
        <span>{item.counterparty_name || "Counterparty not recorded"}</span>
        {item.purchase_order_number && (
          <span className="operations-cell-meta">Purchase order {item.purchase_order_number}</span>
        )}
      </td>
      <td className="operations-align-end">{operationsAmount(item.amount, item.currency)}</td>
      <td>
        <span
          className={`operations-badge ${item.priority === "high" ? "operations-badge-alert" : ""}`}
        >
          {item.status}
        </span>
      </td>
      <td>
        <span>{operationsDate(item.relevant_date || item.submitted_at, timezone)}</span>
        <span className="operations-cell-meta">{item.priority_reason}</span>
      </td>
      <td className="operations-table-action">
        <Link
          className="operations-review"
          to={item.detail_url}
          aria-label={`Review ${label} ${item.number}`}
        >
          Review <span aria-hidden="true">→</span>
        </Link>
      </td>
    </tr>
  );
}

/** @param {Object} props Shipment data and company timezone. @return {JSX.Element} */
function ShipmentRow({ item, timezone }) {
  return (
    <tr
      className={`operations-record${
        item.risk !== "on_track"
          ? " operations-record-priority operations-record-priority--risk"
          : ""
      }`}
    >
      <th scope="row">
        <span className="operations-eyebrow">{item.direction}</span>
        <strong>
          <Link to={item.detail_url}>{item.number}</Link>
        </strong>
        <span className="operations-cell-meta">
          {item.counterparty_name || "Counterparty not recorded"}
        </span>
      </th>
      <td>
        {item.origin || item.destination ? (
          <>
            {item.origin || "Origin not recorded"} <span aria-hidden="true">→</span>{" "}
            {item.destination || "Destination not recorded"}
          </>
        ) : (
          "Route not recorded"
        )}
      </td>
      <td>{operationsDate(item.expected_departure_date, timezone)}</td>
      <td>
        <span>{operationsDate(item.expected_arrival_date, timezone)}</span>
        {item.risk !== "on_track" && (
          <p className="operations-risk">
            {item.risk === "late" ? "Overdue active shipment" : "At risk · delayed"}
          </p>
        )}
      </td>
      <td>
        <span
          className={`operations-badge ${item.risk !== "on_track" ? "operations-badge-alert" : ""}`}
        >
          {item.status.replaceAll("_", " ")}
        </span>
      </td>
      <td className="operations-table-action">
        <Link
          className="operations-review"
          to={item.detail_url}
          aria-label={`Review shipment ${item.number}`}
        >
          Review <span aria-hidden="true">→</span>
        </Link>
      </td>
    </tr>
  );
}

/** @param {Object} props Authorised aggregated snapshot data. @return {JSX.Element} */
function OperationsSnapshots({ snapshots, order }) {
  if (!snapshots || !Object.keys(snapshots).length) return null;
  const snapshotOrder = order?.length
    ? order
    : ["integrations", "stock", "purchasesStatus", "salesStatus", "paymentsStatus"];
  const blocks = {
    integrations: snapshots.integrations && (
      <Card key="integrations">
        <section className="operations-snapshot" aria-labelledby="operations-integrations-title">
          <h2 id="operations-integrations-title">Integration statuses</h2>
          <p className="operations-panel-description">
            Saved connection state and latest sync outcome
          </p>
          <div
            className="operations-snapshot-scroll"
            tabIndex={0}
            role="region"
            aria-label="Integration statuses"
          >
            {snapshots.integrations.length ? (
              <ul className="operations-integration-list">
                {snapshots.integrations.map(item => (
                  <li key={item.id}>
                    <strong>
                      <span
                        className={`operations-integration-dot ${
                          !item.active ? "is-disabled" : item.authenticated ? "is-ok" : "is-warning"
                        }`}
                        aria-hidden="true"
                      />
                      {item.name}
                    </strong>
                    <span className="operations-badge">
                      {!item.active
                        ? "Disabled"
                        : item.authenticated
                          ? "Authenticated"
                          : "Authentication required"}
                    </span>
                    <p>Last sync: {item.last_status || "No recorded sync"}</p>
                    {item.last_sync_at && (
                      <time dateTime={item.last_sync_at}>
                        {new Date(item.last_sync_at).toLocaleString("en-AU")}
                      </time>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <OperationsEmptyPanel
                title="No configured integrations"
                description="Connect an integration to see status here."
                actionLabel="Open integrations"
                actionTo="/integration"
              />
            )}
          </div>
        </section>
      </Card>
    ),
    stock: snapshots.stock && (
      <Card key="stock">
        <section className="operations-snapshot" aria-labelledby="operations-stock-title">
          <h2 id="operations-stock-title">Stock on hand value</h2>
          <p className="operations-panel-description">
            By warehouse / facility · Stored average cost × total stock on hand
            {snapshots.stock.currency
              ? ` · ${snapshots.stock.currency}`
              : " · Currency not configured"}
          </p>
          <div
            className="operations-snapshot-scroll"
            tabIndex={0}
            role="region"
            aria-label="Stock values by facility"
          >
            {snapshots.stock.facilities.length ? (
              <StockBarRows
                facilities={snapshots.stock.facilities}
                currency={snapshots.stock.currency}
              />
            ) : (
              <OperationsEmptyPanel
                title="No active warehouses or facilities"
                description="Stock value will appear when facilities are configured."
              />
            )}
          </div>
        </section>
      </Card>
    ),
    purchasesStatus: snapshots.purchases && (
      <Card key="purchasesStatus">
        <section
          className="operations-snapshot"
          aria-labelledby="operations-purchases-status-title"
        >
          <h2 id="operations-purchases-status-title">Pending purchase orders</h2>
          <p className="operations-panel-description">Open orders by current status</p>
          <div
            className="operations-snapshot-scroll"
            tabIndex={0}
            role="region"
            aria-label="Pending purchase orders by status"
          >
            {snapshots.purchases.length ? (
              <StatusBarRows rows={snapshots.purchases} />
            ) : (
              <OperationsEmptyPanel title="No pending purchase orders" />
            )}
          </div>
        </section>
      </Card>
    ),
    salesStatus: snapshots.sales && (
      <Card key="salesStatus">
        <section className="operations-snapshot" aria-labelledby="operations-sales-status-title">
          <h2 id="operations-sales-status-title">Pending sales orders</h2>
          <p className="operations-panel-description">Open orders by current status</p>
          <div
            className="operations-snapshot-scroll"
            tabIndex={0}
            role="region"
            aria-label="Pending sales orders by status"
          >
            {snapshots.sales.length ? (
              <StatusBarRows rows={snapshots.sales} />
            ) : (
              <OperationsEmptyPanel title="No pending sales orders" />
            )}
          </div>
        </section>
      </Card>
    ),
    paymentsStatus: snapshots.payments && (
      <Card key="paymentsStatus">
        <section className="operations-snapshot" aria-labelledby="operations-payments-title">
          <h2 id="operations-payments-title">
            <Link className="operations-snapshot-heading-link" to="/orders/purchase">
              Purchase order payments
            </Link>
          </h2>
          <p className="operations-panel-description">
            Open purchase orders · Outstanding balance by payment status
          </p>
          <div
            className="operations-snapshot-scroll"
            tabIndex={0}
            role="region"
            aria-label="Purchase order payments by status"
          >
            {snapshots.payments.rows?.length ? (
              <PaymentBarRows rows={snapshots.payments.rows} />
            ) : (
              <OperationsEmptyPanel
                title="No open purchase orders with outstanding payments"
                description="Payment status appears for open orders that still have a balance."
                actionLabel="View purchase orders"
                actionTo="/orders/purchase"
              />
            )}
          </div>
          {snapshots.payments.awaiting_authorisation?.count > 0 && (
            <p className="operations-snapshot-footer">
              <Link to="/payments">
                {snapshots.payments.awaiting_authorisation.count} payment
                {snapshots.payments.awaiting_authorisation.count === 1 ? "" : "s"} awaiting
                authorisation
                {formatOutstandingAmounts(snapshots.payments.awaiting_authorisation.amounts)
                  ? ` · ${formatOutstandingAmounts(snapshots.payments.awaiting_authorisation.amounts)}`
                  : ""}
              </Link>
            </p>
          )}
        </section>
      </Card>
    ),
  };
  const visible = snapshotOrder.map(key => blocks[key]).filter(Boolean);
  if (!visible.length) return null;
  return <div className="operations-snapshots">{visible}</div>;
}

/**
 * Existing home screen: real operational records, review links and honest states.
 * @return {JSX.Element}
 */
export function OperationsControlCentre() {
  useSetupGuideAutoOpen();
  const user = useSelector(state => state.auth.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get("section") || undefined;
  const section = requestedSection === "tasks" ? undefined : requestedSection;
  const offset = Math.max(0, Number(searchParams.get("offset")) || 0);
  const dueFilter = section ? searchParams.get("due") || undefined : undefined;
  const [savedScope, setSavedScope] = useState(() => readTodayScope(user?.id));
  const scope = searchParams.get("scope") || savedScope || undefined;
  const companyTimezone = usePlaceTimezone();
  const localDate = todayCalendarDate(companyTimezone);
  const {
    currentData: data,
    isLoading,
    isFetching,
    error,
    refetch,
    fulfilledTimeStamp,
  } = useGetOperationsOverviewQuery(
    {
      userId: user?.id,
      company: user?.company,
      section,
      offset,
      localDate,
      scope,
      due: dueFilter,
    },
    { refetchOnMountOrArgChange: true },
  );

  const changeTodayScope = nextScope => {
    saveTodayScope(user?.id, nextScope);
    setSavedScope(nextScope);
  };
  const [dismissWelcome, { isLoading: dismissing }] = useDismissOperationsWelcomeMutation();
  const { openSetupGuide } = useSetupGuide();
  const { data: onboarding } = useGetOnboardingQuery(undefined, { skip: !user?.company_id });
  const [updateOnboardingPreferences] = useUpdateOnboardingPreferencesMutation();
  const [dismissed, setDismissed] = useState(false);
  const [dismissError, setDismissError] = useState(false);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [layout, setLayout] = useState(null);
  const attentionRef = useRef(null);

  useEffect(() => {
    setDismissed(false);
    setLayout(null);
  }, [user?.id, user?.company]);
  const openNotes = () => {
    setNotesOpen(true);
    if (!requestedSection || requestedSection === "tasks") {
      setSearchParams({ section: "tasks" }, { replace: true });
    }
  };

  const closeNotes = () => {
    setNotesOpen(false);
    if (requestedSection === "tasks") {
      setSearchParams({}, { replace: true });
    }
  };

  useEffect(() => {
    if (requestedSection === "tasks" && data?.permissions.can_view_notes) {
      setNotesOpen(true);
    }
  }, [requestedSection, data]);
  useEffect(() => {
    const onFocus = () => {
      if (!isFetching && fulfilledTimeStamp && Date.now() - fulfilledTimeStamp > 300000) refetch();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fulfilledTimeStamp, isFetching, refetch]);

  /** @return {Promise<void>} Persist dismissal before hiding the welcome. */
  const onDismiss = async () => {
    setDismissError(false);
    try {
      await dismissWelcome().unwrap();
      setDismissed(true);
    } catch {
      setDismissError(true);
    }
  };

  if (isLoading || (!data && isFetching))
    return (
      <main className="operations">
        <h1>Operations Control Centre</h1>
        <OperationsSkeleton />
      </main>
    );
  if (error)
    return (
      <main className="operations">
        <h1>Operations Control Centre</h1>
        <div className="operations-error" role="alert">
          <p>We could not load your operations overview.</p>
          <OperationsButton onClick={refetch}>Try again</OperationsButton>
        </div>
      </main>
    );
  const visibleDomains = domains.filter(domain => data.permissions[domain.permission]);
  const shipmentCount = (data.summary.shipments || 0) + (data.summary.consignments || 0);
  const countFor = key => (key === "logistics" ? shipmentCount : data.summary[key]);
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hourCycle: "h23",
      timeZone: data.timezone,
    }).format(new Date()),
  );
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const selectedDomain = domains.find(domain => domain.key === section);
  const availableCards = availableDashboardCards(data);
  const appliedLayout = layout ?? loadDashboardLayout(data.user?.id || user?.id, data.company?.id);
  const resolvedLayout = resolveDashboardLayout(
    availableCards.map(card => card.key),
    appliedLayout,
  );
  const visibleLayoutKeys = new Set(
    resolvedLayout.filter(item => item.visible).map(item => item.key),
  );
  const domainByKey = Object.fromEntries(domains.map(domain => [domain.key, domain]));
  const visibleKpis = resolvedLayout
    .filter(item => item.visible && KPI_CARD_KEYS.has(item.key))
    .map(item => domainByKey[item.key])
    .filter(domain => domain && data.permissions[domain.permission]);
  const snapshotOrder = resolvedLayout
    .filter(item => item.visible && SNAPSHOT_CARD_KEYS.has(item.key))
    .map(item => item.key);
  const showAttention = section
    ? section !== "logistics" && visibleDomains.some(domain => domain.key === section)
    : visibleLayoutKeys.has("attention") &&
      visibleDomains.some(domain => domain.key !== "logistics");
  const showShipments =
    (!section || section === "logistics") &&
    data.permissions.can_view_shipments &&
    (Boolean(section) || visibleLayoutKeys.has("shipments"));
  const showComingUp =
    !section &&
    visibleLayoutKeys.has("comingUp") &&
    (data.permissions.can_view_shipments || data.permissions.can_view_notes);
  const panelOrder = section
    ? ["attention", "shipments"]
    : resolvedLayout
        .filter(item => item.visible && PANEL_CARD_KEYS.has(item.key))
        .map(item => item.key);

  /** @param {Object} nextLayout Saved card sequence and hidden keys. */
  const applyLayout = nextLayout => {
    saveDashboardLayout(data.user?.id || user?.id, data.company?.id, nextLayout);
    setLayout(nextLayout);
    setLayoutOpen(false);
  };

  const contentMotionKey = fulfilledTimeStamp || data.generated_at;
  const welcomeStatLines = [
    data.permissions.can_view_sales_orders &&
      (data.summary.sales || 0) > 0 &&
      `${data.summary.sales} wholesale sales orders awaiting confirmation`,
    data.permissions.can_view_purchase_orders &&
      (data.summary.purchases || 0) > 0 &&
      `${data.summary.purchases} purchase orders awaiting confirmation`,
    data.permissions.can_view_receipts &&
      (data.summary.receipts || 0) > 0 &&
      `${data.summary.receipts} purchase receipts waiting for approval`,
    data.permissions.can_view_shipments &&
      countFor("logistics") > 0 &&
      `${countFor("logistics")} shipments in the next 30 days or overdue`,
  ].filter(Boolean);

  return (
    <main className="operations operations--enter" aria-busy={isFetching}>
      <header className="operations-header">
        <div>
          <h1>
            {greeting}
            {data.user.display_name ? `, ${data.user.display_name}` : ""}
          </h1>
        </div>
        <div className="operations-refresh">
          <div className="operations-refresh-actions">
            <OperationsHeaderActions
              isFetching={isFetching}
              onEditLayout={() => setLayoutOpen(true)}
              onRefresh={refetch}
            />
          </div>
        </div>
      </header>
      {data.permissions.can_view_notes && (
        <button type="button" className="operations-notes-tab" onClick={openNotes}>
          {`Tasks (${data.summary.pending_tasks ?? 0}). Overdue (${data.summary.overdue_tasks ?? 0})`}
        </button>
      )}
      <div className="operations-body">
        {!section && (
          <>
            <OperationsTodayStrip
              today={data.today}
              onScopeChange={changeTodayScope}
              onOpenTasks={openNotes}
            />
            <FirstVisitHint hintKey="screen.operations_home" />
          </>
        )}
        {!dismissed && !data.welcome_dismissed_at && (
          <aside className="operations-welcome">
            <div>
              <h2>
                Welcome{data.user.display_name ? `, ${data.user.display_name}` : ""} — your{" "}
                {data.company.name} workspace is ready.
              </h2>
              <p>
                Review the draft orders awaiting confirmation and receipts waiting for approval,
                then see what is arriving and leaving next.
              </p>
              {welcomeStatLines.length > 0 && (
                <ul className="operations-welcome-stats">
                  {welcomeStatLines.map(line => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
              {onboarding?.enabled &&
              onboarding.progress?.total > 0 &&
              onboarding.progress.percent < 100 ? (
                <p className="operations-welcome-setup">
                  Setup: {onboarding.progress.completed} of {onboarding.progress.total} steps
                  complete.{" "}
                  <button
                    type="button"
                    className="operations-welcome-setup-link"
                    onClick={async () => {
                      if (onboarding.hidden) {
                        await updateOnboardingPreferences({ hidden: false }).unwrap();
                      }
                      openSetupGuide();
                    }}
                  >
                    {onboarding.hidden ? "Show setup guide" : "Open setup guide"}
                  </button>
                </p>
              ) : null}
            </div>
            <div className="operations-welcome-actions">
              {showAttention && (
                <OperationsButton
                  onClick={() => {
                    attentionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    attentionRef.current?.focus();
                  }}
                >
                  View what needs attention
                </OperationsButton>
              )}
              <OperationsButton $variant="secondary" onClick={onDismiss} disabled={dismissing}>
                Dismiss
              </OperationsButton>
            </div>
            {dismissError && (
              <p role="alert">We could not save your preference. Try dismissing again.</p>
            )}
          </aside>
        )}
        <div
          className="operations-summary"
          style={{ "--operations-kpi-count": Math.max(visibleKpis.length, 1) }}
        >
          {visibleKpis.map(domain => {
            const count = countFor(domain.key);
            const oldestDays = data.summary_details?.[domain.key]?.oldest_waiting_days;
            return (
              <Card key={domain.key} className="operations-kpi" style={{ padding: 0 }}>
                <Link
                  className={`operations-summary-link${count > 0 ? " operations-summary-link--has-count" : ""}`}
                  to={`/dashboard?section=${domain.key}`}
                  aria-label={`${domain.label}: ${count}. ${domain.state}. Review filtered list.`}
                >
                  <span className="operations-summary-top">
                    <span>{domain.label}</span>
                    <DomainIcon name={domain.key} />
                  </span>
                  <strong>
                    <AnimatedCount value={count} motionKey={contentMotionKey} />
                  </strong>
                  <span className="operations-summary-state">{domain.state}</span>
                  {data.summary_details?.[domain.key] && (
                    <span
                      className={`operations-summary-state ${oldestWaitingStateClass(oldestDays)}`.trim()}
                    >
                      Oldest waiting: {oldestDays} days
                    </span>
                  )}
                </Link>
              </Card>
            );
          })}
        </div>
        {!visibleDomains.length && (
          <p className="operations-empty">
            You do not have access to operational records. Use the navigation to open your available
            modules.
          </p>
        )}
        {showComingUp && <OperationsComingUpCalendar data={data} localDate={localDate} />}
        {!section &&
          visibleDomains.length > 0 &&
          !visibleKpis.length &&
          !showAttention &&
          !showShipments &&
          !showComingUp &&
          !snapshotOrder.length && (
            <p className="operations-empty">
              No cards are selected. Use Edit to choose what you want to see.
            </p>
          )}
        {section && (
          <div className="operations-list-heading">
            <Link to="/dashboard">← Operations overview</Link>
            <h2>{selectedDomain?.label || "Filtered records"}</h2>
            <p>
              {dueFilter || searchParams.get("scope") === "mine"
                ? [
                    searchParams.get("scope") === "mine" && "Assigned to you",
                    dueFilter === "today" && "Due today",
                    dueFilter === "overdue" && "Overdue",
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : `${countFor(section) ?? 0} records in this filtered list`}
            </p>
          </div>
        )}
        <div className={`operations-panels ${section ? "operations-single-panel" : ""}`}>
          {panelOrder.map(key => {
            if (key === "attention" && showAttention) {
              return (
                <Card
                  key="attention"
                  as="section"
                  className="operations-panel"
                  aria-labelledby="operations-attention-title"
                >
                  <h2 id="operations-attention-title" ref={attentionRef} tabIndex={-1}>
                    {section
                      ? `${selectedDomain?.label}: ${selectedDomain?.state}`
                      : "Needs your attention"}
                  </h2>
                  <p className="operations-panel-description">
                    Open a record to review it with its existing confirmation or approval controls.
                  </p>
                  {data.attention_items.length ? (
                    <div
                      className="operations-table-scroll"
                      tabIndex={0}
                      role="region"
                      aria-label="Attention records"
                    >
                      <table className="operations-table">
                        <thead>
                          <tr>
                            <th scope="col">Record</th>
                            <th scope="col">Counterparty</th>
                            <th scope="col" className="operations-align-end">
                              Amount
                            </th>
                            <th scope="col">Status</th>
                            <th scope="col">Needed by</th>
                            <th scope="col">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.attention_items.map(item => (
                            <AttentionRow
                              key={`${item.type}-${item.id}`}
                              item={item}
                              timezone={data.timezone}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <OperationsEmptyPanel
                      title="Nothing awaiting confirmation or approval"
                      description="Draft orders and pending receipts will show up here when they need you."
                    />
                  )}
                </Card>
              );
            }
            if (key === "shipments" && showShipments) {
              return (
                <Card
                  key="shipments"
                  as="section"
                  className="operations-panel"
                  aria-labelledby="operations-shipments-title"
                >
                  <h2 id="operations-shipments-title">Upcoming shipments</h2>
                  <p className="operations-panel-description">
                    Next 30 days, including overdue active shipments.
                  </p>
                  {data.upcoming_shipments.length ? (
                    <div
                      className="operations-table-scroll"
                      tabIndex={0}
                      role="region"
                      aria-label="Upcoming shipment records"
                    >
                      <table className="operations-table">
                        <thead>
                          <tr>
                            <th scope="col">Shipment</th>
                            <th scope="col">Route</th>
                            <th scope="col">Departure</th>
                            <th scope="col">ETA</th>
                            <th scope="col">Status</th>
                            <th scope="col">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.upcoming_shipments.map(item => (
                            <ShipmentRow
                              key={`${item.resource}-${item.id}`}
                              item={item}
                              timezone={data.timezone}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <OperationsEmptyPanel
                      title="No shipments in the next 30 days"
                      description="Planned and active shipments will appear here when they are scheduled."
                      actionLabel="View shipments"
                      actionTo="/shipment/po-to"
                    />
                  )}
                </Card>
              );
            }
            return null;
          })}
        </div>
        {!section && <OperationsSnapshots snapshots={data.snapshots} order={snapshotOrder} />}
        {layoutOpen && (
          <DashboardLayoutModal
            cards={availableCards}
            selectedKeys={resolvedLayout.filter(item => item.visible).map(item => item.key)}
            onApply={applyLayout}
            onClose={() => setLayoutOpen(false)}
          />
        )}
        {notesOpen && <OperationsNotesDrawer onClose={closeNotes} />}
        {section && visibleDomains.some(domain => domain.key === section) && (
          <nav className="operations-pagination" aria-label="Filtered records pagination">
            <OperationsButton
              $variant="secondary"
              disabled={offset === 0 || isFetching}
              onClick={() => setSearchParams({ section, offset: String(Math.max(0, offset - 20)) })}
            >
              Previous
            </OperationsButton>
            <span>
              {countFor(section)
                ? `${offset + 1}–${Math.min(offset + 20, countFor(section))} of ${countFor(section)}`
                : "0 records"}
            </span>
            <OperationsButton
              $variant="secondary"
              disabled={offset + 20 >= countFor(section) || isFetching}
              onClick={() => setSearchParams({ section, offset: String(offset + 20) })}
            >
              Next
            </OperationsButton>
          </nav>
        )}
      </div>
    </main>
  );
}
